import type { TFunction } from 'i18next';
import type { ApprovalLapseReason, WorkOrderSignerRole } from '@/types';

export function signatureMethodLabel(t: TFunction, method: string): string {
  switch (method) {
    case 'counter_tablet':
      return t('signature.methodCounterTablet');
    case 'paper':
      return t('signature.methodPaper');
    case 'phone':
      return t('signature.methodPhone');
    case 'override':
      return t('signature.methodOverride');
    case 'remote_link':
      return t('signature.methodRemoteLink');
    case 'portal':
      return t('signature.methodPortal');
    default:
      return method;
  }
}

export function signerRoleLabel(t: TFunction, role: WorkOrderSignerRole | string): string {
  switch (role) {
    case 'orderer':
      return t('signature.roleOrderer');
    case 'billing_party':
      return t('signature.roleBillingParty');
    case 'driver':
      return t('signature.roleDriver');
    default:
      return role;
  }
}

export function priceBasisLabel(t: TFunction, basis: string): string {
  switch (basis) {
    case 'fixed':
      return t('signature.priceBasisFixed');
    case 'estimate':
      return t('signature.priceBasisEstimate');
    case 'hourly':
      return t('signature.priceBasisHourly');
    default:
      return basis;
  }
}

/**
 * Whether the newest ownership check still counts for the identity on the work
 * order now (#3889). `matchesRegistry` alone only says what the check found at the
 * time; only `valid` is current evidence. Anything the server did not classify is
 * treated as not current, so a historical green match is never shown as valid.
 */
export type OwnershipEvidenceState =
  | 'none'
  | 'valid'
  | 'mismatch'
  | 'stale'
  | 'unbound'
  | 'identity_missing';

interface OwnershipEvidenceFields {
  latestOwnershipVerification: {
    matchesRegistry: boolean;
    evidenceState?: string;
  } | null;
  ownershipEvidenceState?: string;
}

const EVIDENCE_STATES: readonly string[] = [
  'none',
  'valid',
  'mismatch',
  'stale',
  'unbound',
  'identity_missing',
];

export function ownershipEvidenceState(status: OwnershipEvidenceFields): OwnershipEvidenceState {
  const latest = status.latestOwnershipVerification;
  if (latest == null) return 'none';
  const reported = latest.evidenceState ?? status.ownershipEvidenceState;
  if (reported !== undefined && EVIDENCE_STATES.includes(reported)) {
    return reported as OwnershipEvidenceState;
  }
  // An answer without a classification cannot be current evidence.
  return latest.matchesRegistry ? 'unbound' : 'mismatch';
}

export function ownershipEvidenceLabel(
  t: TFunction,
  state: OwnershipEvidenceState,
  documentType: string | null,
): string {
  const document = documentType ? ownershipDocumentLabel(t, documentType) : '';
  switch (state) {
    case 'valid':
      return t('signature.ownershipValid', { document });
    case 'mismatch':
      return t('signature.ownershipMismatch', { document });
    case 'stale':
      return t('signature.ownershipStale', { document });
    case 'unbound':
      return t('signature.ownershipUnbound', { document });
    case 'identity_missing':
      return t('signature.ownershipIdentityMissing');
    default:
      return t('signature.ownershipNone');
  }
}

export function ownershipDocumentLabel(t: TFunction, documentType: string): string {
  switch (documentType) {
    case 'id_card':
      return t('signature.documentIdCard');
    case 'passport':
      return t('signature.documentPassport');
    case 'driving_licence':
      return t('signature.documentDrivingLicence');
    case 'company_authorization':
      return t('signature.documentCompanyAuthorization');
    default:
      return documentType;
  }
}

export function lapseReasonLabel(t: TFunction, reason: ApprovalLapseReason | string): string {
  switch (reason) {
    case 'scope_added':
      return t('signature.lapseScopeAdded');
    case 'gross_increase_over_limit':
      return t('signature.lapseGrossIncrease');
    case 'price_basis_changed':
      return t('signature.lapsePriceBasis');
    default:
      return reason;
  }
}

export function toDateTimeLocalValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
